import { useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { FaTrash } from "react-icons/fa6";
import IconButton from "../../../../components/common/form/IconButton";
import ModalButton from "../../../../components/common/form/ModalButton";
import { useDeleteSubDivisionByIdMutation, useGetSubDivisionByIdQuery, useUpdateSubDivisionMutation } from "../subDivisionApi";
import { useNavigate, useParams } from "react-router-dom";
import { Routes as AppRoutes } from "../../../../routes";
import SubdivisionDetailInfo from "./SubdivisionDetailInfo";
import SubdivisionParentAccount from "./SubdivisionParentAccount";
import SubdivisionChildAccount from "./SubdivisionChildAccount";
import Associations from "./SubdivisionAssociations";
import SubdivisionStatusSection from "./SudvisionStatusSection";
import ResearchNotesForm from "./SubidivisionResearchNotes";
import SubdivisionCertificateNote from "./SubdivisionCertificateNote";
import SubdivisionDocuments from "./SubdivisionDocuments";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import ChildAccountNotes from "./SubdivisionChildAccountNotes";

const SubdivisionDetailPage = () => {

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Subdivisions", url: "/subdivisions" },
        { content: "Subdivisions-Detail", url: "" }]
    )
    let { subdivisionId } = useParams();
    const navigate = useNavigate();
    const subdivisionDataById = useGetSubDivisionByIdQuery(subdivisionId);
    const [deleteApi, { isLoading }] = useDeleteSubDivisionByIdMutation();
    const [subdivisionData, setSubdivisionData] = useState<any>({});
    const [aliases, setAliases,] = useState([]);
    const [showButtons, setShowButtons] = useState<boolean>(false);
    const [updateApi, updateApiResponse] = useUpdateSubDivisionMutation();
    const { data: enums } = useGetEnumsQuery("");

    useEffect(() => {
        document.title = 'Subdivisions';

    }, []);

    const handleDelete = async () => {
        if (subdivisionId) {
            await deleteApi(subdivisionId).unwrap();
        }
        navigate(AppRoutes.Subdivision.path)
    };
    useEffect(() => {
        let obj = subdivisionDataById?.data;
        const researchStatusOptions = createOptionListForSelectTag(enums?.["SubdivisionResearchStatus"], "constant", "value");
        const rollbackStatusOptions = createOptionListForSelectTag(enums?.["SubdivisionRollbackStatus"], "constant", "value");
        const endOfYearStatusOptions = createOptionListForSelectTag(enums?.["EndOfYearStatus"], "constant", "value");
        const associationPresenceOptions = createOptionListForSelectTag(enums?.["AssociationPresence"], "constant", "value");

        if (obj) {
            setSubdivisionData({
                ...obj,
                state: {
                    label: obj?.county?.state?.stateName || "",
                    value: obj?.county?.state?.id || "",
                },
                counties: [
                    {
                        label: obj?.county?.countyName || "",
                        value: obj?.county?.id || "",
                    }
                ],
                name: obj?.subdivisionKey?.name,
                unit: obj?.subdivisionKey?.unit,
                phase: obj?.subdivisionKey?.phase,
                section: obj?.subdivisionKey?.section,
                platStatus: obj?.platStatus ? {
                    value: obj?.platStatus,
                    label: getEnumDisplayNameByConstant(enums?.["PlatStatus"], obj?.platStatus)
                } : null,
                subdivisionResearchStatus: obj?.subdivisionResearchStatus ? {
                    value: obj?.subdivisionResearchStatus || null,
                    label: researchStatusOptions.find(opt => opt?.label === obj?.subdivisionResearchStatus)?.value || null,
                } : null,
                subdivisionRollbackStatus: obj?.subdivisionRollbackStatus ? {
                    value: obj?.subdivisionRollbackStatus || null,
                    label: rollbackStatusOptions.find(opt => opt.label === obj?.subdivisionRollbackStatus)?.value || null,
                } : null,
                endOfYearStatus: obj?.endOfYearStatus ? {
                    value: obj?.endOfYearStatus || null,
                    label: endOfYearStatusOptions.find(opt => opt.label === obj?.endOfYearStatus)?.value || null,
                } : null,

                associationPresence: obj?.associationPresence ? {
                    value: obj?.associationPresence || null,
                    label: associationPresenceOptions.find(opt => opt.label === obj?.associationPresence)?.value || null,
                } : null,

            });
            setAliases(obj?.aliases.map((alias: any) => ({
                label: alias,
                value: alias
            })));
        }
    }, [subdivisionDataById?.data, enums]);

    const handleSubmit = async (e: any) => {
        const obj: any = {
            ...subdivisionData,
            subdivisionKey: {
                name: subdivisionData?.name,
                unit: subdivisionData?.unit,
                phase: subdivisionData?.phase,
                section: subdivisionData?.section
            },
            stateAbbr: subdivisionData?.state?.value,
            countyId: subdivisionData?.counties[0]?.value,
            aliases: aliases.map((alias: any) => alias?.value),
            associationPresence: subdivisionData?.associationPresence?.value,
            subdivisionResearchStatus: subdivisionData?.subdivisionResearchStatus?.value,
            subdivisionRollbackStatus: subdivisionData?.subdivisionRollbackStatus?.value,
            endOfYearStatus: subdivisionData?.endOfYearStatus?.value,
            platStatus: subdivisionData?.platStatus?.value,
            expectedTaxYearAlt: subdivisionData?.expectedTaxYearAlt !== "" && subdivisionData?.expectedTaxYearAlt !== null ? parseInt(subdivisionData?.expectedTaxYearAlt) : null,
            expectedTaxYear: subdivisionData?.expectedTaxYear !== "" && subdivisionData?.expectedTaxYear !== null ? parseInt(subdivisionData?.expectedTaxYear) : null,
        }

        await handleRTKQuery(
            async () => {
                const response = await updateApi({
                    id: subdivisionId,
                    requestBody: obj,
                    countyId: subdivisionData?.counties[0]?.value
                }).unwrap();

                setShowButtons(false); // ✅ Only runs after API call succeeds
                return response;
            }
        );

    };

    return (
        <>
            <div className="px-5 py-5">
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {
                            hasPermission(["SUBD-D"]) &&
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash />}
                                        title={"Delete Subdivision"}
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title={"Delete Subdivision"}
                                        bodyMessage={"Are you sure you want to delete this Subdivision?"}
                                        onSubmit={() => handleDelete()}
                                        isLoading={isLoading}
                                    />
                                }
                            />}
                        {
                            hasPermission(["AUDIT-V"]) &&
                            <AuditableInfoButton obj={subdivisionDataById?.data} />}
                    </div>
                </div>
                <div className="md:grid grid-cols-9 gap-4 mt-3">

                    <div className="col-span-5 space-y-4">
                        <SubdivisionDetailInfo
                            subdivisionData={subdivisionData}
                            setShowButtons={setShowButtons}
                            showButtons={showButtons}
                            setAliases={setAliases}
                            aliases={aliases}
                            onSubmit={() => handleSubmit(subdivisionData)}
                            setSubdivisionData={setSubdivisionData}
                            subdivisionsData={subdivisionData}
                            updateApiResponse={updateApiResponse}
                            isReadOnly={!hasPermission(["SUBD-U"])}
                        />
                        <SubdivisionParentAccount parentAccountData={subdivisionDataById?.data?.parentAccountList} />
                        <SubdivisionChildAccount />
                        <Associations subdivisionData={subdivisionDataById?.data} />

                    </div>
                    <div className="col-span-4">
                        <SubdivisionStatusSection subdivisionData={subdivisionData} setShowButtons={setShowButtons} showButtons={showButtons}
                            setSubdivisionData={setSubdivisionData} subdivisionsData={subdivisionData} />
                        <ResearchNotesForm />
                        <ChildAccountNotes />
                        {hasPermission(["CRTNOT-V"]) && <SubdivisionCertificateNote parentAccountData={subdivisionDataById?.data?.parentAccountList} />}
                        <SubdivisionDocuments />
                    </div>
                </div>
            </div>
        </>
    )
};


export default SubdivisionDetailPage;